import React from "react";
import { useFormikContext } from "formik";

interface ErrorItem {
  path: string;
  message: string;
}

// Helper function to make field names more readable
const formatFieldName = (fieldPath: string): string => {
  return fieldPath
    .replace(/\[(\d+)\]/g, " #$1") // Replace [0] with #0
    .replace(/\./g, " → ") // Replace dots with arrows
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

const FormErrorSummary: React.FC = () => {
  const { errors, touched, isValid } = useFormikContext<any>();

  // Function to collect all errors recursively
  const collectErrors = (
    errors: any,
    touched: any,
    path: string = ""
  ): ErrorItem[] => {
    const errorMessages: ErrorItem[] = [];

    // Add null/undefined checks
    if (!errors || typeof errors !== "object") {
      return errorMessages;
    }

    Object.keys(errors).forEach((key) => {
      // Check if the key actually exists in the errors object
      if (!(key in errors)) {
        return;
      }

      const currentPath = path ? `${path}.${key}` : key;
      const error = errors[key];
      const touchedField = touched?.[key]; // Add optional chaining

      if (typeof error === "string") {
        errorMessages.push({
          path: currentPath,
          message: error,
        });
      } else if (error && typeof error === "object") {
        if (Array.isArray(error)) {
          // Handle array errors (like field arrays)
          error.forEach((item, index) => {
            if (item && typeof item === "object") {
              const arrayPath = `${currentPath}[${index}]`;
              const arrayTouched = Array.isArray(touchedField)
                ? touchedField[index]
                : {};
              errorMessages.push(
                ...collectErrors(item, arrayTouched, arrayPath)
              );
            } else if (typeof item === "string" && touchedField?.[index]) {
              errorMessages.push({
                path: `${currentPath}[${index}]`,
                message: item,
              });
            }
          });
        } else {
          // Handle nested object errors
          const nestedTouched =
            touchedField && typeof touchedField === "object"
              ? touchedField
              : {};
          errorMessages.push(
            ...collectErrors(error, nestedTouched, currentPath)
          );
        }
      }
    });

    return errorMessages;
  };

  // Only show if form has been touched and has errors
  const hasBeenTouched = touched && Object.keys(touched).length > 0;
  const allErrors =
    hasBeenTouched && errors ? collectErrors(errors, touched) : [];

  if (!hasBeenTouched || !allErrors.length) {
    return null;
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-red-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">
            {allErrors.length === 1
              ? "Please fix the following error:"
              : `Please fix the following ${allErrors.length} errors:`}
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul className="list-disc pl-5 space-y-1">
              {allErrors.map((error, index) => (
                <li key={index}>
                  <span className="font-medium">
                    {formatFieldName(error.path)}:
                  </span>{" "}
                  {error.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormErrorSummary;
